/**
 * Patient booking flow: clinic, visit type, date, slot, discount, deposit.
 *
 * Consolidates `openPatientBookingCalendar`, `renderPatientGlobalCalendar`,
 * `selectPatientBookingDate`, `selectPatientBookingSlotModal`,
 * `handlePatientClinicChange`, `selectBookingType`, `applyDiscountCode`,
 * `applyBMVoucher`, `_recalcBMPrice`, `updateBookingDetailsUI`,
 * `handleDepositReceiptUpload` and `submitPatientBooking` — roughly 1,100 lines
 * of the legacy patient dashboard, most of it duplicated between the inline
 * calendar and the modal variant of the same flow.
 *
 * All arithmetic lives in `domain/pricing`; this component only orchestrates.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  PageHeader,
  Panel,
  Select,
  Spinner,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { useClinicDayBookings } from '../../hooks/useBookings';
import { clinicApi, bookingApi } from '../../api/endpoints';
import { BOOKING_TYPES, quoteBooking, formatCurrency } from '../../domain/pricing';
import { buildBookingPayload } from '../../domain/bookings';
import { buildSlotAvailability } from '../../domain/slots';
import { bestDayDiscount, nightDiscountFor, validateVoucher } from '../../domain/offers';
import { validateBookingSubmission } from '../../domain/validation';
import { formatTime12, todayKey, isoToDisplayDate } from '../../domain/dates';
import { BookingCalendar } from './BookingCalendar';
import { compressImage } from '../../domain/files';


export function PatientBooking() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const { session, patientId } = useAuth();
  const { findDoctor, doctorsState } = useData();
  const { notify, success, error: toastError } = useNotifications();

  const doctor = findDoctor(doctorId);

  /* --- clinic list ------------------------------------------------------ */
  // The doctor record from /api/doctors already embeds Clinics, but without
  // their extra_services; /api/clinics returns those, and the services drive the
  // price display, so the dedicated call is the one to trust.
  const clinicsState = useAsync(() => clinicApi.listByDoctor(doctorId), [doctorId], {
    initialData: [],
    enabled: Boolean(doctorId),
  });
  const clinics = useMemo(() => clinicsState.data || [], [clinicsState.data]);

  /* --- selection state -------------------------------------------------- */
  const [clinicId, setClinicId] = useState(null);
  const [bookingType, setBookingType] = useState(BOOKING_TYPES.EXAM);
  const [dateKey, setDateKey] = useState(todayKey());
  const [slot, setSlot] = useState(null);
  const [voucherInput, setVoucherInput] = useState('');
  const [voucherResult, setVoucherResult] = useState(null);
  // Ids only. The clinic rows are the source of truth for names and prices,
  // and the backend reprices from its own tables regardless.
  const [serviceIds, setServiceIds] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Default to the first clinic once they load.
  useEffect(() => {
    if (!clinicId && clinics.length > 0) setClinicId(clinics[0].id);
  }, [clinics, clinicId]);

  // Extras belong to one clinic. Carrying a tick across a clinic change would
  // bill for a service the new clinic does not offer.
  useEffect(() => {
    setServiceIds([]);
  }, [clinicId]);

  const clinic = useMemo(
    () => clinics.find((c) => String(c.id) === String(clinicId)) || null,
    [clinics, clinicId],
  );

  const clinicServices = useMemo(() => clinic?.extra_services || [], [clinic]);

  /**
   * The ticked extras as full rows.
   *
   * Derived rather than stored: a service the doctor deleted, or one belonging
   * to the clinic the patient just switched away from, drops out here instead
   * of lingering in the total as a line nobody can see.
   */
  const chosenServices = useMemo(
    () => clinicServices.filter((service) => serviceIds.includes(String(service.id))),
    [clinicServices, serviceIds],
  );

  const toggleService = (serviceId) =>
    setServiceIds((current) =>
      current.includes(String(serviceId))
        ? current.filter((id) => id !== String(serviceId))
        : [...current, String(serviceId)],
    );

  /* --- availability ----------------------------------------------------- */
  const dayBookings = useClinicDayBookings(clinicId, dateKey);

  /** The evening block, if the doctor opened one for this clinic on this day. */
  const extraBlock = useMemo(
    () =>
      (doctor?.extraHours || []).find(
        (entry) => entry.date === dateKey && String(entry.clinicId) === String(clinicId),
      ) || null,
    [doctor, dateKey, clinicId],
  );

  const slots = useMemo(
    () =>
      buildSlotAvailability(
        clinic,
        dateKey,
        dayBookings.data || [],
        new Date(),
        extraBlock?.slots || [],
      ),
    [clinic, dateKey, dayBookings.data, extraBlock],
  );

  /** A closed day cannot be booked, whatever its slots say. */
  const dayIsClosed = useMemo(
    () => (doctor?.cancelledDays || []).includes(dateKey),
    [doctor, dateKey],
  );

  // Drop a chosen slot that a change of clinic/date/other-patient invalidated.
  useEffect(() => {
    if (!slot) return;
    const stillFree = slots.find((s) => s.time === slot && s.available);
    if (!stillFree) setSlot(null);
  }, [slots, slot]);

  /**
   * Every booking this doctor holds, for the availability counts on the grid.
   *
   * The calendar shows three months and GET /bookings has no range filter, so
   * this is one call for the doctor's whole list, sliced per month by
   * `buildMonthStats`. `useClinicDayBookings` above stays as it is: it is the
   * authority for the slot grid, and it refetches when the chosen day changes.
   */
  const doctorBookings = useAsync(() => bookingApi.list({ doctorId }), [doctorId], {
    initialData: [],
    enabled: Boolean(doctorId),
  });

  const calendarBookings = useMemo(() => doctorBookings.data || [], [doctorBookings.data]);

  /* --- pricing ---------------------------------------------------------- */

  /**
   * The undiscounted price of each visit type, for the two choice cards.
   *
   * Deliberately quoted without the day/night/voucher discounts: the cards are
   * a comparison between كشف and متابعة, and folding a discount into them would
   * show a number that changes when the patient picks a different day. The
   * discounted total is in the summary panel.
   */
  const typePrices = useMemo(
    () =>
      Object.fromEntries(
        Object.values(BOOKING_TYPES).map((type) => [
          type,
          quoteBooking({ doctor, clinic, bookingType: type }).finalPrice,
        ]),
      ),
    [doctor, clinic],
  );

  const quote = useMemo(() => {
    const dayDiscount = bestDayDiscount(doctor?.offers || [], dateKey);
    const nightDiscount = nightDiscountFor({
      doctor,
      dateKey,
      isNightSlot: Boolean(slot && Number(String(slot).split(':')[0]) >= 18),
      isExtraHourSlot: Boolean(slot && (extraBlock?.slots || []).includes(slot)),
    });
    return quoteBooking({
      doctor,
      clinic,
      bookingType,
      dayDiscount,
      nightDiscount,
      voucherDiscount: voucherResult?.valid ? voucherResult.percent : 0,
      selectedServices: chosenServices,
    });
  }, [doctor, clinic, bookingType, dateKey, slot, voucherResult, extraBlock, chosenServices]);

  /* --- handlers --------------------------------------------------------- */

  const applyVoucher = () => {
    const result = validateVoucher(voucherInput, doctor?.voucherCodes || []);
    setVoucherResult(result.message ? result : null);
  };

  const handleReceipt = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const dataUri = await compressImage(file, { maxWidth: 1400, quality: 0.72 });
      setReceipt(dataUri);
      setErrors((current) => ({ ...current, depositReceipt: undefined }));
    } catch (err) {
      toastError(`تعذر قراءة صورة الإيصال: ${err.message}`);
    }
  };

  const handleSubmit = async () => {
    const validation = validateBookingSubmission({
      dateKey,
      time: slot,
      depositRequired: quote.deposit.required,
      depositReceipt: receipt,
    });
    setErrors(validation.errors);
    if (!validation.valid) return;

    setSubmitting(true);
    try {
      const payload = buildBookingPayload({
        doctor,
        clinic,
        patient: {
          id: patientId,
          name: session?.name,
          phone: session?.phone,
          username: session?.username,
        },
        dateKey,
        time: slot,
        bookingType,
        quote,
        voucherCode: voucherResult?.valid ? voucherInput.trim().toUpperCase() : null,
        depositReceipt: receipt,
        bookedBy: 'patient',
      });

      await bookingApi.create(payload);

      notify({
        type: 'success',
        title: 'تم الحجز بنجاح ✅',
        message: `${bookingType === BOOKING_TYPES.FOLLOWUP ? 'متابعة' : 'كشف'} عند ${doctor.name} يوم ${dateKey} الساعة ${formatTime12(slot)}.`,
        action: '/patient/appointments',
        icon: 'calendar-check',
      });
      success('تم تأكيد الحجز');
      navigate('/patient/appointments');
    } catch (err) {
      // 409 is the backend's "someone took this slot while you were deciding".
      if (err.status === 409) {
        toastError('هذا الموعد تم حجزه للتو من مريض آخر. يرجى اختيار موعد آخر.');
        setSlot(null);
        dayBookings.reload();
      } else {
        toastError(err.message || 'فشل الحجز، حاول مرة أخرى.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* --- render ----------------------------------------------------------- */

  if (doctorsState.loading && !doctor) return <Spinner label="جاري تحميل بيانات الطبيب..." />;

  if (!doctor) {
    return (
      <Panel>
        <EmptyState
          icon="user-x"
          title="لم يتم العثور على الطبيب"
          message="ربما تم تعليق الحساب أو حذفه."
          action={
            <Button variant="ghost" onClick={() => navigate('/patient/doctors')}>
              العودة لقائمة الأطباء
            </Button>
          }
        />
      </Panel>
    );
  }

  const canSubmit = Boolean(slot && clinic) && !submitting && !dayIsClosed;

  return (
    <div className="space-y-6">
      <PageHeader
        title="حجز موعد"
        subtitle={doctor.name}
        icon="calendar-plus"
        actions={
          <Button variant="ghost" icon="arrow-right" onClick={() => navigate(-1)}>
            رجوع
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-6">
          {/* Doctor summary */}
          <Panel>
            <div className="flex items-center gap-4">
              <Avatar src={doctor.image} name={doctor.name} size={64} />
              <div className="min-w-0">
                <h2 className="truncate text-lg font-bold text-white">{doctor.name}</h2>
                <p className="truncate text-sm text-slate-400">{doctor.title || doctor.categoryName}</p>
              </div>
            </div>
          </Panel>

          {/* Clinic + visit type */}
          <Panel>
            <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
              <Icon name="building-2" size={18} className="text-cyan-400" />
              العيادة ونوع الزيارة
            </h3>

            {clinicsState.error ? (
              <ErrorState error={clinicsState.error} onRetry={clinicsState.reload} />
            ) : clinicsState.loading ? (
              <Spinner variant="inline" label="جاري تحميل العيادات..." />
            ) : clinics.length === 0 ? (
              <EmptyState
                icon="building"
                title="لا توجد عيادات متاحة"
                message="لم يقم الطبيب بإضافة عيادات بعد."
              />
            ) : (
              <div className="space-y-4">
                <Field label="العيادة">
                  <Select value={clinicId ?? ''} onChange={(e) => setClinicId(e.target.value)}>
                    {clinics.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.address ? ` — ${c.address}` : ''}
                      </option>
                    ))}
                  </Select>
                </Field>

                {/* Visit type — the legacy's two priced cards, so the choice
                    is made against the price rather than after it. */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: BOOKING_TYPES.EXAM, label: 'كشف', icon: 'clipboard-plus' },
                    { value: BOOKING_TYPES.FOLLOWUP, label: 'متابعة', icon: 'repeat' },
                  ].map((option) => {
                    const active = bookingType === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setBookingType(option.value)}
                        className={`flex flex-col items-center justify-center gap-2 rounded-2xl border p-5 transition-colors ${
                          active
                            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                            : 'border-white/10 bg-slate-900/60 text-slate-400 hover:border-white/20 hover:text-slate-200'
                        }`}
                      >
                        <Icon
                          name={option.icon}
                          size={26}
                          className={active ? 'text-emerald-400' : 'text-slate-500'}
                        />
                        <span className="text-base font-bold">{option.label}</span>
                        <span
                          className={`text-sm font-bold ${active ? 'text-emerald-400' : 'text-slate-500'}`}
                        >
                          {formatCurrency(typePrices[option.value])}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Extras were a price list you could only read. Ticking them
                    here puts them on the booking, so the clinic knows what to
                    prepare and the patient is not quoted one number and asked
                    for another at the desk. */}
                {clinicServices.length > 0 && (
                  <div className="rounded-xl border border-white/5 bg-white/5 p-3">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs font-bold text-slate-300">خدمات إضافية بالعيادة</p>
                      <p className="text-[11px] text-slate-500">اختياري — تُضاف للإجمالي</p>
                    </div>

                    <ul className="space-y-1.5">
                      {clinicServices.map((service) => {
                        const id = String(service.id);
                        const chosen = serviceIds.includes(id);
                        return (
                          <li key={id}>
                            <button
                              type="button"
                              onClick={() => toggleService(id)}
                              aria-pressed={chosen}
                              className={`flex w-full items-center gap-2.5 rounded-lg border p-2.5 text-right transition-colors ${
                                chosen
                                  ? 'border-emerald-500/60 bg-emerald-500/10'
                                  : 'border-white/10 bg-slate-900/60 hover:border-white/20'
                              }`}
                            >
                              <span
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                  chosen
                                    ? 'border-emerald-500 bg-emerald-500 text-slate-900'
                                    : 'border-white/25'
                                }`}
                              >
                                {chosen && <Icon name="check" size={11} />}
                              </span>

                              <span
                                className={`min-w-0 flex-1 truncate text-xs font-bold ${
                                  chosen ? 'text-emerald-200' : 'text-slate-300'
                                }`}
                              >
                                {service.service_name_ar}
                              </span>

                              <span
                                className={`shrink-0 text-xs font-bold ${
                                  chosen ? 'text-emerald-400' : 'text-slate-400'
                                }`}
                              >
                                {formatCurrency(service.service_price)}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>

                    {quote.servicesTotal > 0 && (
                      <p className="mt-2 text-left text-[11px] font-bold text-emerald-300">
                        {chosenServices.length} خدمة — {formatCurrency(quote.servicesTotal)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </Panel>

          {/* Date — the month grid, as the legacy booking calendar showed it */}
          <div>
            <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
              <Icon name="calendar-days" size={18} className="text-emerald-400" />
              اختر يوم الموعد
            </h3>

            {doctorBookings.loading && calendarBookings.length === 0 ? (
              <Spinner variant="inline" label="جاري تحميل المواعيد..." />
            ) : (
              <BookingCalendar
                clinics={clinics}
                bookings={calendarBookings}
                offers={doctor?.offers || []}
                extraHours={doctor?.extraHours || []}
                cancelledDays={doctor?.cancelledDays || []}
                selectedDate={dateKey}
                onSelectDay={(nextDate, dayClinicId) => {
                  setDateKey(nextDate);
                  setSlot(null);
                  // The grid tints a day by its first working clinic; selecting
                  // that day should show that clinic's slots, not whichever one
                  // happened to be chosen before.
                  if (dayClinicId) setClinicId(dayClinicId);
                }}
              />
            )}
          </div>

          {/* Slots */}
          <Panel>
            <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
              <Icon name="clock" size={18} className="text-cyan-400" />
              المواعيد المتاحة — {isoToDisplayDate(dateKey)}
            </h3>

            {dayIsClosed ? (
              <EmptyState
                icon="calendar-x"
                title="هذا اليوم مغلق"
                message="أغلق الطبيب هذا اليوم. اختر يوماً آخر من التقويم بالأعلى."
              />
            ) : dayBookings.loading ? (
              <Spinner variant="inline" label="جاري التحقق من المواعيد..." />
            ) : slots.length === 0 ? (
              <EmptyState
                icon="calendar-x"
                title="لا توجد مواعيد في هذا اليوم"
                message="اختر يوماً آخر من التقويم بالأعلى."
              />
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5">
                {slots.map((entry) => {
                  // Evening slots keep the gold treatment they have in the
                  // doctor's day modal, so the two screens describe the same
                  // slot the same way.
                  const extraStyle =
                    entry.isExtra && entry.available && slot !== entry.time
                      ? { background: '#2d1f00', borderColor: '#7d5a00', color: '#fde047' }
                      : undefined;

                  return (
                    <button
                      key={entry.time}
                      type="button"
                      disabled={!entry.available}
                      onClick={() => setSlot(entry.time)}
                      style={extraStyle}
                      className={`rounded-xl border p-2.5 text-xs font-bold transition-colors ${
                        slot === entry.time
                          ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200'
                          : entry.available
                            ? 'border-white/10 bg-slate-900 text-slate-200 hover:border-cyan-500/50 hover:bg-slate-800'
                            : 'cursor-not-allowed border-white/5 bg-slate-950 text-slate-600 line-through'
                      }`}
                      title={
                        entry.isPast
                          ? 'هذا الموعد قد فات'
                          : entry.available
                            ? `${entry.isExtra ? 'موعد مسائي إضافي — ' : ''}متاح (${entry.capacity - entry.taken} من ${entry.capacity})`
                            : 'محجوز بالكامل'
                      }
                    >
                      {entry.isExtra && <span className="ml-1">🌙</span>}
                      {formatTime12(entry.time)}
                    </button>
                  );
                })}
              </div>
            )}

            {errors.time && (
              <p className="mt-3 text-xs font-bold text-red-400">{errors.time}</p>
            )}
          </Panel>
        </div>

        {/* Summary rail */}
        <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          <Panel>
            <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
              <Icon name="receipt" size={18} className="text-emerald-400" />
              ملخص الحجز
            </h3>

            <dl className="space-y-2 text-sm">
              <Row label="الموعد" value={slot ? `${isoToDisplayDate(dateKey)} — ${formatTime12(slot)}` : 'لم يتم الاختيار'} />
              <Row label="العيادة" value={clinic?.name || '—'} />
              <Row label="النوع" value={bookingType === BOOKING_TYPES.FOLLOWUP ? 'متابعة' : 'كشف'} />
            </dl>

            <div className="my-4 h-px bg-white/10" />

            <dl className="space-y-2 text-sm">
              <Row
                label="السعر الأساسي"
                value={
                  quote.discountAmount > 0 ? (
                    <span className="text-slate-500 line-through">{formatCurrency(quote.basePrice)}</span>
                  ) : (
                    formatCurrency(quote.basePrice)
                  )
                }
              />
              {quote.breakdown.day > 0 && (
                <Row label={`خصم اليوم (${quote.breakdown.day}%)`} value="—" accent="emerald" />
              )}
              {quote.breakdown.night > 0 && (
                <Row label={`خصم مسائي (${quote.breakdown.night}%)`} value="—" accent="emerald" />
              )}
              {quote.breakdown.voucher > 0 && (
                <Row label={`كود خصم (${quote.breakdown.voucher}%)`} value="—" accent="emerald" />
              )}
              {/* Each extra on its own line: a total that silently grew by 150
                  reads as a pricing bug, not as a service the patient chose. */}
              {quote.services.map((service) => (
                <Row
                  key={service.id}
                  label={service.name}
                  value={`+ ${formatCurrency(service.price)}`}
                />
              ))}

              <div className="flex items-center justify-between pt-1">
                <dt className="text-sm font-bold text-white">الإجمالي</dt>
                <dd className="text-xl font-bold text-emerald-400">
                  {formatCurrency(quote.grandTotal)}
                </dd>
              </div>
              {quote.discountAmount > 0 && (
                <p className="text-left text-[11px] text-emerald-300">
                  وفّرت {formatCurrency(quote.discountAmount)}
                </p>
              )}
            </dl>
          </Panel>

          {/* Voucher */}
          <Panel>
            <Field label="كود الخصم">
              <div className="flex gap-2">
                <Input
                  value={voucherInput}
                  onChange={(event) => {
                    setVoucherInput(event.target.value);
                    setVoucherResult(null);
                  }}
                  placeholder="أدخل الكود"
                  dir="ltr"
                  className="flex-1"
                />
                <Button variant="ghost" onClick={applyVoucher} disabled={!voucherInput.trim()}>
                  تطبيق
                </Button>
              </div>
            </Field>
            {voucherResult?.message && (
              <p
                className={`mt-2 text-xs font-bold ${
                  voucherResult.valid ? 'text-emerald-400' : 'text-red-400'
                }`}
              >
                {voucherResult.message}
              </p>
            )}
          </Panel>

          {/* Deposit */}
          {quote.deposit.required && (
            <Panel className="border border-amber-500/30">
              <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-amber-300">
                <Icon name="wallet" size={18} />
                عربون مطلوب
              </h3>

              <dl className="mb-3 space-y-1.5 text-sm">
                <Row label="قيمة العربون" value={formatCurrency(quote.deposit.amount)} />
                <Row label="النسبة" value={`${quote.deposit.percent}%`} />
                <Row label="حساب InstaPay" value={quote.deposit.instaPayAccount || 'غير محدد'} />
              </dl>

              <p className="mb-3 rounded-lg bg-amber-500/10 p-2.5 text-[11px] leading-relaxed text-amber-200">
                حوّل قيمة العربون ثم ارفع صورة الإيصال. لن يتم تأكيد الحجز قبل مراجعة العيادة
                للإيصال.
                {quote.servicesTotal > 0 &&
                  ' العربون محسوب على سعر الكشف فقط — تُدفع الخدمات الإضافية في العيادة.'}
              </p>

              {receipt && (
                <img
                  src={receipt}
                  alt="إيصال التحويل"
                  className="mb-3 max-h-40 w-full rounded-lg border border-white/10 object-contain"
                />
              )}

              <input
                type="file"
                accept="image/*"
                onChange={handleReceipt}
                className="w-full text-xs text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-200"
              />

              {errors.depositReceipt && (
                <p className="mt-2 text-xs font-bold leading-relaxed text-red-400">
                  {errors.depositReceipt}
                </p>
              )}
            </Panel>
          )}

          <Button
            className="w-full !py-3"
            variant="success"
            icon="check"
            disabled={!canSubmit}
            loading={submitting}
            onClick={handleSubmit}
          >
            تأكيد الحجز
          </Button>

          {!slot && (
            <p className="text-center text-[11px] text-slate-500">اختر موعداً متاحاً لتفعيل الحجز</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd
        className={`truncate text-xs font-bold ${accent === 'emerald' ? 'text-emerald-400' : 'text-slate-200'}`}
      >
        {value}
      </dd>
    </div>
  );
}

export default PatientBooking;
